import isEmpty from "lodash/isEmpty";

import appConfig from "@/config/app-config";
import { type Account } from "@/schemas";
import KVStore from "@/storage/kv-store";

interface App {
  client_id: string;
  client_secret: string;
}

interface Tokens {
  [accessToken: string]: {
    me: string; // account url
    account: string; // account id
  } & App;
}

interface Users {
  [url: string]: {
    access_token: string;
    id: string; // account id
    url: string; // profile url
  };
}

interface Credentials {
  me: string | null; // profile url
  tokens: Tokens;
  users: Users;
}

/**
 * Legacy localStorage key for auth. Needed to seamlessly
 * migrate users to new key without logging them out.
 */
const buildKey = (parts: string[]) => parts.join(":");
const NAMESPACE = "soapbox";
const DEPRECATED_AUTH_KEY = buildKey([NAMESPACE, "auth"]);

const LOCAL_STORAGE_AUTH_KEY = "truth:auth";
const LOCAL_STORAGE_EXTERNAL_APP_KEY = "truth:external:app";
const LOCAL_STORAGE_EXTERNAL_BASE_URL_KEY = "truth:external:baseurl";
const LOCAL_STORAGE_EXTERNAL_SCOPES_KEY = "truth:external:scopes";
const KV_STORE_KEY_PREFIX = "authAccount";
const emptyLocalStorage = {
  tokens: {},
  users: {},
  me: null,
};

const authScopes = "read write follow push";

// ===============
// Misc
// ===============

function isURL(url?: string | null) {
  if (typeof url !== "string") return false;

  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

// ================
// Getters
// ================

function getCachedAccountUrlHost() {
  const accountUrl = getCachedAccountUrl() as string;

  try {
    return new URL(accountUrl).origin;
  } catch {
    return window.location.origin;
  }
}

function getBaseURL(): string {
  return isURL(appConfig.BACKEND_URL)
    ? appConfig.BACKEND_URL
    : getCachedAccountUrlHost();
}

/**
 * Get localStorage "auth" details
 * @returns Credentials
 */
function getCachedAuth(): Credentials {
  let cachedAuthString = localStorage.getItem(LOCAL_STORAGE_AUTH_KEY);

  if (!cachedAuthString) {
    const deprecatedCachedAuthString =
      localStorage.getItem(DEPRECATED_AUTH_KEY);

    if (deprecatedCachedAuthString) {
      localStorage.setItem(LOCAL_STORAGE_AUTH_KEY, deprecatedCachedAuthString);
      cachedAuthString = localStorage.getItem(LOCAL_STORAGE_AUTH_KEY);
    } else {
      cachedAuthString = JSON.stringify(emptyLocalStorage);
      localStorage.setItem(LOCAL_STORAGE_AUTH_KEY, cachedAuthString);
    }
  }

  if (cachedAuthString) {
    return JSON.parse(cachedAuthString);
  }

  return emptyLocalStorage;
}

/**
 * Get the "users" key from localStorage
 * @returns Users[0][]
 */
function getCachedAccountsArray(): Users[0][] {
  const cachedAuth = getCachedAuth();
  const users = cachedAuth.users;

  if (isEmpty(users)) {
    return [];
  }

  return Object.keys(users).map((key) => users[key]);
}

/**
 * Get logged in user from localStorage
 */
function getCachedAccount(): Users[0] | null {
  const cachedAuth = getCachedAuth();
  if (!cachedAuth) return null;

  return cachedAuth.users[cachedAuth.me!];
}

/**
 * Get logged in user URL from localStorage
 * @returns String | undefined
 */
function getCachedAccountUrl(): string | undefined {
  const account = getCachedAccount();
  return account?.url;
}

/**
 * Get logged in user token from localStorage
 * @returns String | undefined
 */
function getCachedAccountToken(): string | undefined {
  const account = getCachedAccount();
  return account?.access_token;
}

/**
 * Get logged in user id from localStorage
 * @returns String | undefined
 */
function getCachedAccountId(): string | undefined {
  const account = getCachedAccount();
  return account?.id;
}

/**
 * Get the account token from the account URL.
 * @param url String
 *
 * @returns string | null
 */
function getAccountTokenFromUrl(url: string): string | null {
  const localStorageObject = getCachedAuth();

  for (const token in localStorageObject.tokens) {
    if (localStorageObject.tokens[token].me === url) {
      return token;
    }
  }

  return null;
}

/**
 * Get the client_id/client_secret from the cached Token object
 */
function getCachedAccountApp(): App & { token: string } {
  const token = getCachedAccountToken()!;
  const object = getCachedAuth();
  const { client_id, client_secret } = object.tokens[token!];

  return { client_id, client_secret, token };
}

/**
 * Pull the account from IndexedDB store
 */
async function getAccountFromIndexedDb(): Promise<Account | null> {
  const account = getCachedAccount();
  const accountFromIndexDb = await KVStore.getItem(
    `${KV_STORE_KEY_PREFIX}:${account?.url}`,
  );

  return accountFromIndexDb as Account | null;
}

// ================
// Setters
// ================

/**
 * Cache the "me" string (user's URL) in local storage
 * @returns undefined
 */
function cacheAccountUrl(url: string): undefined {
  const object = getCachedAuth();
  object.me = url;
  localStorage.setItem(LOCAL_STORAGE_AUTH_KEY, JSON.stringify(object));
}

/**
 * Cache the entire user in local storage
 * @returns undefined
 */
function cacheAccount(account: Account, token?: string, app?: App): undefined {
  const object = getCachedAuth();

  if (token && app) {
    object.me = account.url;
    object.tokens[token] = { account: account.id, me: account.url, ...app };
    object.users[account.url] = {
      access_token: token,
      id: account.id,
      url: account.url,
    };

    localStorage.setItem(LOCAL_STORAGE_AUTH_KEY, JSON.stringify(object));
  }

  KVStore.setItem(`${KV_STORE_KEY_PREFIX}:${account.url}`, account);
}

/**
 * Remove a user from local storage. Essentially, we have two logged in accounts here.
 * When the current user wants to log out of one of them, we need to remove it from
 * local storage and set the other user to the current user.
 *
 * @returns undefined
 */
function uncacheAccount(account: Account, nextAccount: Account) {
  const object = getCachedAuth();
  const token = getAccountTokenFromUrl(account.url);

  // set the new user
  object.me = nextAccount.url;

  // delete the old user
  delete object.tokens[token!];
  delete object.users[account.url];

  localStorage.setItem(LOCAL_STORAGE_AUTH_KEY, JSON.stringify(object));
}

export {
  authScopes,
  cacheAccount,
  cacheAccountUrl,
  getAccountFromIndexedDb,
  getBaseURL,
  getCachedAccountApp,
  getCachedAccountId,
  getCachedAccountToken,
  getCachedAccountUrl,
  getCachedAccountUrlHost,
  getCachedAccountsArray,
  getCachedAuth,
  isURL,
  uncacheAccount,

  // constants
  DEPRECATED_AUTH_KEY,
  LOCAL_STORAGE_AUTH_KEY,
  LOCAL_STORAGE_EXTERNAL_APP_KEY,
  LOCAL_STORAGE_EXTERNAL_BASE_URL_KEY,
  LOCAL_STORAGE_EXTERNAL_SCOPES_KEY,

  // types
  type App,
};
